import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import {
  CodeContext,
  CommandType,
  IntegrationPlatform,
  integrationPlatformFromJSON
} from '~/protos/agents'
import { QueryKeys } from '~/services/queryClient'
import { getFlowNames, getRepoMetadata } from '~/services/taskService'

interface UseRepoMetadataProps {
  connType: string
  orgName: string
  repoName: string
  branchName: string
  commitId: string
  s3TarFilePath: string
  currentOption?: string
  enabled?: boolean
  platform?: IntegrationPlatform
  commandType?: CommandType
}

// Safely extract mule flow names from various response shapes
function extractMuleAllFlowNames(data: unknown): string[] {
  if (data == null) return []

  // Shape A: { flows: { muleAllFlowNames: string[] } }
  // Shape B: { flows: { flows: { muleAllFlowNames: string[] } } }
  // Shape C: { muleAllFlowNames: string[] }
  const outer = data as { flows?: unknown; muleAllFlowNames?: unknown }
  const firstLevel = (outer.flows ?? outer) as {
    muleAllFlowNames?: unknown
    flows?: unknown
  }
  const secondLevel = (firstLevel.flows ?? {}) as { muleAllFlowNames?: unknown }

  const candidate =
    firstLevel.muleAllFlowNames !== undefined
      ? firstLevel.muleAllFlowNames
      : secondLevel.muleAllFlowNames

  return Array.isArray(candidate) ? (candidate as unknown as string[]) : []
}

export const useRepoMetadata = ({
  connType,
  orgName,
  repoName,
  branchName,
  commitId,
  s3TarFilePath,
  currentOption = 'codeContext',
  enabled = true,
  platform,
  commandType
}: UseRepoMetadataProps) => {
  const { currentWorkspace } = useWorkspaceContext()

  const hasRequiredFields =
    enabled &&
    Boolean(currentWorkspace?.uuid) &&
    Boolean(
      (connType && orgName && repoName && branchName && currentOption === 'codeContext') ||
        (s3TarFilePath && currentOption === 'upload')
    )

  const codeContext: CodeContext = {
    connType,
    orgName,
    repoName,
    branchName,
    commitId,
    s3TarFilePath
  }

  // Local state to manage displayed VM args - clears on context change
  const [displayedVmArgs, setDisplayedVmArgs] = useState<string>('')

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      codeContext,
      'repoMetadata',
      currentOption,
      platform,
      commandType,
      ...QueryKeys.Conversations
    ],
    queryFn: ({ queryKey }) => {
      if (!currentWorkspace?.uuid) {
        throw new Error('Workspace not found')
      }
      return getRepoMetadata(
        currentWorkspace.uuid,
        queryKey[0] as CodeContext,
        platform,
        commandType
      )
    },
    enabled: hasRequiredFields,
    refetchOnWindowFocus: false,
    meta: {
      persist: false
    },
    select: data => {
      if (!data) {
        return {
          flowNames: null,
          suggestedVmArgs: '',
          suggestedJavaVersion: '',
          suggestedMvnVersion: '',
          suggestedMinMuleRuntimeVersion: '',
          detectedPlatform: IntegrationPlatform.UNKNOW_INTEGRATION_PLATFORM
        }
      }

      // Extract flow names from the response
      const flowNames = extractMuleAllFlowNames(data.flows)
      const suggestedVmArgs = data.suggestedVmArgs || ''

      // Extract runtime version suggestions from the flows object
      // Response structure: { flows: { suggestedJavaVersion, suggestedMvnVersion, ... } }
      const flowsData = (data.flows as Record<string, unknown>) || {}
      const suggestedJavaVersion = (flowsData.suggestedJavaVersion as string) || ''
      const suggestedMvnVersion = (flowsData.suggestedMvnVersion as string) || ''
      const suggestedMinMuleRuntimeVersion =
        (flowsData.suggestedMinMuleRuntimeVersion as string) || ''

      // Extract detected platform from repo metadata response
      // MessageToDict returns enum as string name (e.g. "INFORMATICA"), not a number
      const rawPlatform = flowsData.detectedPlatform
      const detectedPlatform: IntegrationPlatform = rawPlatform
        ? integrationPlatformFromJSON(rawPlatform)
        : IntegrationPlatform.UNKNOW_INTEGRATION_PLATFORM

      // Extract suggested end system info (applications)
      const suggestedEndSystemInfo =
        (flowsData.suggestedEndSystemInfo as Array<{
          systemObjectName: string
          suggestedSchemaInfo: string
        }>) || []

      return {
        flowNames: Array.isArray(flowNames) && flowNames.length > 0 ? flowNames : [],
        suggestedVmArgs,
        suggestedJavaVersion,
        suggestedMvnVersion,
        suggestedMinMuleRuntimeVersion,
        detectedPlatform,
        applications: suggestedEndSystemInfo
      }
    }
  })

  // Clear displayed VM args when query parameters change (new fetch triggered)
  // or when query becomes disabled (missing required fields)
  // Update with fresh data when it arrives
  useEffect(() => {
    if (!hasRequiredFields) {
      // Clear immediately when switching contexts without complete info
      setDisplayedVmArgs('')
    } else if (isFetching) {
      // Clear when new fetch starts
      setDisplayedVmArgs('')
    } else if (data?.suggestedVmArgs) {
      // Update with fresh data
      setDisplayedVmArgs(data.suggestedVmArgs)
    }
  }, [hasRequiredFields, isFetching, data?.suggestedVmArgs])

  return {
    flowNames: data?.flowNames ?? null,
    suggestedVmArgs: displayedVmArgs,
    suggestedJavaVersion: data?.suggestedJavaVersion ?? '',
    suggestedMvnVersion: data?.suggestedMvnVersion ?? '',
    suggestedMinMuleRuntimeVersion: data?.suggestedMinMuleRuntimeVersion ?? '',
    detectedPlatform: data?.detectedPlatform ?? IntegrationPlatform.UNKNOW_INTEGRATION_PLATFORM,
    applications: data?.applications ?? [],
    isLoading,
    isFetching
  }
}

/**
 * Lightweight hook that fetches only flow names via direct GitHub API access.
 * Much faster than useRepoMetadata because it bypasses the agent service's
 * clone + index pipeline.  Falls back to the agent gRPC call for S3 uploads
 * and non-GitHub connection types.
 */
interface UseFlowNamesProps {
  connType: string
  orgName: string
  repoName: string
  branchName: string
  s3TarFilePath: string
  currentOption?: string
  enabled?: boolean
}

export const useFlowNames = ({
  connType,
  orgName,
  repoName,
  branchName,
  s3TarFilePath,
  currentOption = 'codeContext',
  enabled = true
}: UseFlowNamesProps) => {
  const { currentWorkspace } = useWorkspaceContext()

  const hasRequiredFields =
    enabled &&
    Boolean(currentWorkspace?.uuid) &&
    Boolean(
      (connType && orgName && repoName && branchName && currentOption === 'codeContext') ||
        (s3TarFilePath && currentOption === 'upload')
    )

  const codeContext: CodeContext = {
    connType,
    orgName,
    repoName,
    branchName,
    commitId: '',
    s3TarFilePath
  }

  const { data, isFetching } = useQuery({
    queryKey: [codeContext, 'flowNames'],
    queryFn: () => {
      if (!currentWorkspace?.uuid) {
        throw new Error('Workspace not found')
      }
      return getFlowNames(currentWorkspace.uuid, codeContext)
    },
    enabled: hasRequiredFields,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes — flow names rarely change
    meta: { persist: false },
    select: data => data?.flowNames ?? []
  })

  return {
    flowNames: data ?? [],
    isFetching
  }
}

export default useRepoMetadata
